/** Fachliche UI-Labels – nie rohe Enum-/DB-Werte im UI. */

import type { Database } from "@/types/database";

type Enums = Database["public"]["Enums"];

export const ANIMAL_STATUS_LABEL: Record<Enums["case_status"], string> = {
  in_care: "In Pflege",
  hibernating: "Überwinterung",
  released: "Ausgewildert",
  deceased: "Verstorben",
  voided: "Ungültig",
};

export const NOTIFICATION_CATEGORY_LABEL: Record<Enums["notification_category"], string> = {
  care_due: "Fällig",
  own_cases: "Eigene Fälle",
  access: "Zugriff",
  animal_feed: "Tiere",
  mentions: "Erwähnung",
  handover: "Übergabe",
  calendar: "Kalender",
  club: "Verein",
};

export const PRIVACY_PROVISION_LABEL: Record<Enums["privacy_provision_kind"], string> = {
  spoken: "Mündlich",
  written: "Schriftlich",
  printed: "Ausdruck",
  digital: "Digital",
  other: "Sonstiges",
};

export function handoverAnimalLabel(
  name: string | null | undefined,
  caseNumber: string | null | undefined,
): string {
  const trimmed = name?.trim() ?? "";
  const number = caseNumber?.trim() ?? "";
  if (trimmed && number) return `${trimmed} · ${number}`;
  return trimmed || number || "Tier";
}

export const HANDOVER_STATUS_LABEL: Record<Enums["handover_status"], string> = {
  pending: "Offen",
  accepted: "Angenommen",
  cancelled: "Abgebrochen",
  rejected: "Abgelehnt",
  admin_forced: "Durch Verwaltung",
};

export const CARE_SITE_KIND_LABEL: Record<Enums["care_site_kind"], string> = {
  home: "Zuhause",
  station: "Station",
  external_hibernate: "Externe Überwinterung",
  other: "Sonstiges",
};

export const ACCESS_ROLE_LABEL: Record<Enums["care_site_access_role"], string> = {
  staff: "Mitarbeit",
  manager: "Verwaltung",
};

export const MEMBERSHIP_ROLE_LABEL: Record<Enums["membership_role"], string> = {
  caregiver: "Pflege",
  medicus: "Tiermedizin",
  board: "Vorstand",
  organizer: "Organisation",
  admin: "Administration",
};

export const DIAGNOSIS_STATE_LABEL: Record<Enums["diagnosis_episode_state"], string> = {
  suspicion: "Verdacht",
  confirmed: "Bestätigt",
  ruled_out: "Ausgeschlossen",
  in_treatment: "In Behandlung",
  closed: "Abgeschlossen",
};

export const DEATH_CAUSE_LABEL: Record<Enums["death_cause"], string> = {
  unknown: "Unbekannt",
  illness: "Erkrankung",
  injury_accident: "Verletzung/Unfall",
  euthanasia: "Euthanasie",
  other: "Sonstiges",
};

export const CONTACT_ROLE_LABEL: Record<Enums["contact_role"], string> = {
  finder: "Finder",
  courier: "Transport",
  release_contact: "Auswilderungskontakt",
  external_hibernate: "Externe Überwinterung",
  other: "Sonstiges",
};

export const CASE_RELATION_TYPE_LABEL: Record<Enums["case_relation_type"], string> = {
  mother_of: "Mutter von",
  sibling_group: "Geschwistergruppe",
  same_identity_suspected: "Identität vermutet",
  same_identity_confirmed: "Identität bestätigt",
};

export const PLACE_NODE_KIND_LABEL: Record<Enums["place_node_kind"], string> = {
  site_root: "Standort",
  building: "Gebäude",
  room: "Raum",
  shelf: "Regal",
  cage: "Käfig",
  box: "Box",
  enclosure: "Gehege",
  area: "Bereich",
  other: "Sonstiges",
};

export const LOADING_LABEL = "Laden…";

export const CARE_TASK_KIND_LABEL: Record<Enums["care_task_rule_kind"], string> = {
  daily_check: "Kontrolle",
  weight: "Gewicht",
};

export const RHYTHM_KIND_LABEL: Record<Enums["rhythm_kind"], string> = {
  interval: "Intervall",
  daily_times: "Feste Uhrzeiten",
  once: "Einmalig",
  as_needed: "Bei Bedarf",
};

export const CARE_PANEL_LABEL = {
  openTasksEmpty: "Keine offenen Aufgaben.",
  dailyCheckHint:
    "Die Schaltfläche schließt die tägliche Kontrolle ab. Eine Notiz, ein Gewicht oder ein Medikament allein reicht dafür nicht.",
  intervalsTitle: "Intervalle für dieses Tier",
  intervalsHint:
    "Ersetzt die Vereinsvorgaben für Kontrolle und Gewicht. Fällige Medikamente bleiben aus den Plänen.",
  saveIntervals: "Vorgaben speichern",
  checkEveryHours: "Kontrollabstand in Stunden",
  weightEveryHours: "Wiegeabstand in Stunden",
  housingHint:
    "Bewusste Aktionen. Der Platzwechsel bleibt in der aktuellen Pflegestelle. Die Pflegestelle selbst wird über die Übergabe gewechselt.",
  therapyTitle: "Therapie",
  therapyHint: "Aktive Pläne und Hinweise.",
  inactive: "inaktiv",
} as const;

export const CARE_EVENT_TYPE_LABEL: Record<Enums["care_event_type"], string> = {
  weight: "Gewicht",
  free_text: "Notiz",
  diagnosis_change: "Diagnose",
  place_change: "Unterbringung",
  status_change: "Status",
  closure_released: "Auswilderung",
  closure_deceased: "Verstorben",
  intake_snapshot: "Erstaufnahme",
  image_ref: "Bild",
  medication_dose: "Medikament",
  treatment: "Behandlung",
  examination: "Untersuchung",
  plan_change: "Plan",
  care_hint_change: "Hinweis",
  care_site_change: "Pflegestelle",
};

export function labelOf<K extends string>(
  map: Record<K, string>,
  value: string | null | undefined,
  fallback = "–",
): string {
  if (!value) return fallback;
  if (!Object.prototype.hasOwnProperty.call(map, value)) return fallback;
  return map[value as K] ?? fallback;
}
